/** @jsxImportSource @emotion/react */

import type { ReactNode } from "react";
import { useEffect } from "react";
import { Link } from "@reach/router";
import { useTranslation } from "react-i18next";
import HelpPanel from "../HelpPanel";
import type { HelpScreenId } from "./helpScreens";

import type { GameState, MoveDirection } from "src/core/types";
import { reset } from "src/core/baoside";
import {
  logPageView,
  logTutorialComplete,
  logTutorialNavigatedBackward,
  logTutorialNavigatedForward
} from "../../hooks/use-analytics";
import { Helmet } from "react-helmet";
import type { Theme } from "src/theme";

type Props = {
  path?: string;
  screenIndex?: string;
};

// Per-screen board configuration. Titles and instruction prose are localised
// (see help.screens.<id>.* translation keys); only the non-textual game state
// lives here. Order matches HELP_SCREEN_IDS / the :screenIndex route param.
type Help = {
  id: HelpScreenId;
  gameState: GameState;
  moveIndex: number;
  moveDirection: MoveDirection;
  highlightedHoles?: { "0": number[]; "1": number[] };
};
const helpData: Help[] = [
  {
    id: "introduction",
    gameState: [reset(), reset()],
    moveIndex: -1,
    moveDirection: 1
  },
  {
    id: "validMoves",
    gameState: [reset(), reset()],
    moveIndex: 8,
    moveDirection: -1
  },
  {
    id: "clockwiseSowing",
    gameState: [reset(), [0, 1, 0, 1, 8, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
    moveIndex: 4,
    moveDirection: 1
  },
  {
    id: "antiClockwiseSowing",
    gameState: [reset(), [0, 1, 0, 1, 8, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
    moveIndex: 4,
    moveDirection: -1
  },
  {
    id: "capturing",
    gameState: [reset(), [2, 0, 2, 0, 2, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]],
    moveIndex: 0,
    moveDirection: 1,
    highlightedHoles: { "1": [0, 2, 4, 6], "0": [] }
  },
  {
    id: "kimbiKichwa",
    gameState: [reset(), reset()],
    moveIndex: 2,
    moveDirection: -1,
    highlightedHoles: { "0": [0, 1, 6, 7], "1": [] }
  },
  {
    id: "mandatoryCapturing",
    gameState: [
      [7, 0, 3, 0, 0, 0, 0, 0, 0, 0, 9, 0, 0, 2, 1, 1],
      [0, 2, 0, 2, 5, 0, 4, 8, 0, 2, 1, 2, 3, 0, 0, 0, 0]
    ],
    moveIndex: 9,
    moveDirection: -1
  },
  {
    id: "takataTurn",
    gameState: [
      [0, 0, 3, 0, 0, 0, 0, 3, 0, 0, 9, 0, 0, 2, 1, 1],
      [0, 2, 0, 2, 5, 0, 4, 8, 0, 1, 1, 12, 3, 0, 0, 0, 0]
    ],
    moveIndex: 1,
    moveDirection: 1
  },
  {
    id: "gameEnding",
    gameState: [
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 1, 4, 4, 3, 2]
    ],
    moveIndex: -1,
    moveDirection: 1
  }
];

const HelpSection = (props: Props): ReactNode => {
  const { t } = useTranslation();
  // Get screen index from route param, default to 0. A non-numeric param (e.g.
  // /game/help/abc) parses to NaN — coerce to 0 so the component never indexes
  // helpData with NaN (which would yield undefined and crash on help.id).
  const parsedIndex = props.screenIndex ? parseInt(props.screenIndex, 10) : 0;
  const helpIndex = Number.isNaN(parsedIndex) ? 0 : parsedIndex;
  const validIndex = Math.max(0, Math.min(helpIndex, helpData.length - 1));
  const help = helpData[validIndex];
  const title = t(`help.screens.${help.id}.title`);
  const instruction = t(`help.screens.${help.id}.instruction`);

  useEffect(() => {
    logPageView("Bao | How to play", window.location.href);
    logTutorialNavigatedForward(validIndex);
    if (validIndex === helpData.length - 1) {
      logTutorialComplete();
    }
  }, [validIndex]);

  const hasPrevious = validIndex > 0;
  const hasNext = validIndex < helpData.length - 1;
  const previousTitle = hasPrevious ? t(`help.screens.${helpData[validIndex - 1].id}.title`) : null;
  const nextTitle = hasNext ? t(`help.screens.${helpData[validIndex + 1].id}.title`) : null;

  const navigationStyle = ({ gridUnit }: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: gridUnit * 5
  });

  const navLinkStyle = ({ gridUnit, colors }: Theme) => ({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    textDecoration: "none",
    borderRadius: gridUnit * 2,
    transition: "background-color 0.2s",
    paddingTop: gridUnit * 2,
    paddingBottom: gridUnit * 2,
    color: colors.grey3,
    flex: 1,
    ":hover": {
      backgroundColor: colors.grey95
    }
  });

  const navLinkLeftStyle = (theme: Theme) => ({
    ...navLinkStyle(theme),
    alignItems: "flex-start" as const
  });

  const navLinkRightStyle = (theme: Theme) => ({
    ...navLinkStyle(theme),
    alignItems: "flex-end" as const
  });

  const navLabelStyle = ({ gridUnit, colors }: Theme) => ({
    fontSize: gridUnit * 3,
    fontWeight: "600",
    color: colors.grey1,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: gridUnit
  });

  const navTitleStyle = ({ fontSize, colors }: Theme) => ({
    fontSize: fontSize,
    fontWeight: "500",
    color: colors.primary
  });

  return (
    <div
      css={({ boardHeight, boardWidth }: Theme) => ({
        background: "white",
        transformOrigin: "top left",
        display: "flex",
        flexDirection: "column" as const
      })}
    >
      <Helmet>
        {/* Brand prefix is intentionally kept in English (matches the analytics
            label below and the brand name); only the screen title is localised. */}
        <title>Bawo | How to Play | {title}</title>
        <meta name="description" content={`${title} | ${instruction}`} />
      </Helmet>
      <HelpPanel
        instruction={instruction}
        gameState={help.gameState}
        moveIndex={help.moveIndex}
        moveDirection={help.moveDirection}
        highlightedHoles={help.highlightedHoles}
      />
      <nav css={navigationStyle}>
        {hasPrevious ? (
          <Link
            data-testid="previous"
            to={`/game/help/${validIndex - 1}`}
            css={navLinkLeftStyle}
            onClick={() => logTutorialNavigatedBackward(validIndex - 1)}
          >
            <span css={navLabelStyle}>← {t("help.previous")}</span>
            <span css={navTitleStyle}>{previousTitle}</span>
          </Link>
        ) : (
          <div css={{ flex: 1 }} />
        )}
        {hasNext ? (
          <Link
            data-testid="next"
            to={`/game/help/${validIndex + 1}`}
            css={navLinkRightStyle}
            onClick={() => logTutorialNavigatedForward(validIndex + 1)}
          >
            <span css={navLabelStyle}>{t("help.next")} →</span>
            <span css={navTitleStyle}>{nextTitle}</span>
          </Link>
        ) : (
          <div css={{ flex: 1 }} />
        )}
      </nav>
    </div>
  );
};

export default HelpSection;
